import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { FiPercent, FiArrowRight, FiCheck, FiTrendingDown, FiClock, FiRefreshCw } from "@/lib/icons";
import {
  majorBankRates,
  rbaCashRateHistory as staticCashRateHistory,
  ratesLastUpdated,
  type LenderRate,
} from "./_data/bankRates";
import { CashRateChart } from "./_components/CashRateChart";
import { cn } from "@/lib/utils";
import { getLenderLogo } from "@/config/lender-logos.config";

type CashRateHistoryItem = {
  id?: string;
  date: string;
  rate: number;
  effectiveDate?: string;
};

type RateView = "variable" | "fixed";
type FixedTerm = "oneYear" | "twoYear" | "threeYear" | "fiveYear";

const fixedTermLabels: Record<FixedTerm, string> = {
  oneYear: "1 Year",
  twoYear: "2 Year",
  threeYear: "3 Year",
  fiveYear: "5 Year",
};

/**
 * Current Rates Page
 */
export function RatesPage() {
  const [rateView, setRateView] = useState<RateView>("variable");
  const [fixedTerm, setFixedTerm] = useState<FixedTerm>("twoYear");
  const [lenderRates, setLenderRates] = useState<LenderRate[]>(majorBankRates);
  const [cashRateHistory, setCashRateHistory] = useState<CashRateHistoryItem[]>(staticCashRateHistory);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [isLoadingCashRate, setIsLoadingCashRate] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Fetch rates from API
  const fetchRates = useCallback(async () => {
    setIsLoadingRates(true);
    try {
      const res = await fetch("/api/rates/by-lender");
      if (res.ok) {
        const data = await res.json() as LenderRate[];
        if (data && data.length > 0) {
          setLenderRates(data);
          setLastUpdated(new Date());
        }
      }
    } catch (error) {
      console.error("Failed to fetch rates, using static data:", error);
    } finally {
      setIsLoadingRates(false);
    }
  }, []);

  // Fetch cash rate history from API
  const fetchCashRateHistory = useCallback(async () => {
    setIsLoadingCashRate(true);
    try {
      const res = await fetch("/api/rates/cash-rate-history");
      if (res.ok) {
        const data = await res.json() as Array<{
          id: string;
          effectiveDate: string;
          rate: number;
          description: string | null;
        }>;
        if (data && data.length > 0) {
          // Transform DB format to chart format
          const formatted = data
            .sort((a, b) => new Date(a.effectiveDate).getTime() - new Date(b.effectiveDate).getTime())
            .map((item) => ({
              id: item.id,
              date: new Date(item.effectiveDate).toLocaleDateString("en-AU", {
                month: "short",
                year: "numeric",
              }),
              rate: Number(item.rate),
              effectiveDate: item.effectiveDate,
            }));
          setCashRateHistory(formatted);
        }
      }
    } catch (error) {
      console.error("Failed to fetch cash rate history, using static data:", error);
    } finally {
      setIsLoadingCashRate(false);
    }
  }, []);

  // Refresh all data
  const refreshAll = useCallback(() => {
    fetchRates();
    fetchCashRateHistory();
  }, [fetchRates, fetchCashRateHistory]);

  // Initial fetch
  useEffect(() => {
    fetchRates();
    fetchCashRateHistory();
  }, [fetchRates, fetchCashRateHistory]);

  // Calculate current rate and changes
  const currentCashRate = useMemo(() => {
    if (cashRateHistory.length === 0) return 3.60;
    return cashRateHistory[cashRateHistory.length - 1].rate;
  }, [cashRateHistory]);

  const peakRate = useMemo(() => {
    if (cashRateHistory.length === 0) return 4.35;
    return Math.max(...cashRateHistory.map((r) => r.rate));
  }, [cashRateHistory]);

  const rateReduction = useMemo(() => {
    return (peakRate - currentCashRate).toFixed(2);
  }, [peakRate, currentCashRate]);

  // Calculate lowest rates
  const lowestVariable = lenderRates.length > 0
    ? Math.min(...lenderRates.map((r) => r.variableRate).filter((r) => r > 0))
    : 0;
  const lowestFixed = lenderRates.length > 0
    ? Math.min(
        ...lenderRates.flatMap((r) => [
          r.fixedRates.oneYear,
          r.fixedRates.twoYear,
          r.fixedRates.threeYear,
          r.fixedRates.fiveYear,
        ]).filter((r) => r > 0)
      )
    : 0;

  // Filter and sort rates by current view - only show lenders with valid rates
  const sortedRates = [...lenderRates]
    .filter((lender) => {
      if (rateView === "variable") {
        return lender.variableRate > 0;
      }
      return lender.fixedRates[fixedTerm] > 0;
    })
    .sort((a, b) => {
      if (rateView === "variable") {
        return a.variableRate - b.variableRate;
      }
      return a.fixedRates[fixedTerm] - b.fixedRates[fixedTerm];
    });

  return (
    <>
      <SEO
        title="Current Home Loan Rates"
        description="Compare the latest home loan interest rates from major Australian lenders including CBA, Westpac, NAB, ANZ, and more."
        url="/rates"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex p-3 bg-accent/20 rounded-xl mb-6">
              <FiPercent className="size-6 text-primary" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Current Home Loan Rates
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
              Compare the latest interest rates from Australia's leading lenders.
              We have access to 70+ lenders to find you the best deal.
            </p>
            <div className="flex items-center justify-center gap-4">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <FiClock className="size-4" />
                Last updated: {lastUpdated ? lastUpdated.toLocaleString() : ratesLastUpdated}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshAll}
                disabled={isLoadingRates || isLoadingCashRate}
                className="gap-2"
              >
                <FiRefreshCw className={cn("size-4", (isLoadingRates || isLoadingCashRate) && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Rate Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-background rounded-2xl p-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">Lowest Variable Rate</p>
              <p className="text-4xl font-bold text-primary">{lowestVariable.toFixed(2)}%</p>
              <p className="text-xs text-muted-foreground mt-2">Comparison rate may differ</p>
            </div>
            <div className="bg-background rounded-2xl p-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">Lowest Fixed Rate</p>
              <p className="text-4xl font-bold text-primary">{lowestFixed.toFixed(2)}%</p>
              <p className="text-xs text-muted-foreground mt-2">Across all terms</p>
            </div>
            <div className="bg-background rounded-2xl p-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">RBA Cash Rate</p>
              <p className="text-4xl font-bold text-primary">
                {currentCashRate.toFixed(2)}%
              </p>
              <p className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
                <FiTrendingDown className="size-3 text-green-600" />
                Down from {peakRate.toFixed(2)}% peak
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Rate Table */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex gap-2">
              <button
                onClick={() => setRateView("variable")}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium transition-colors",
                  rateView === "variable"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                Variable Rates
              </button>
              <button
                onClick={() => setRateView("fixed")}
                className={cn(
                  "px-4 py-2 rounded-lg font-medium transition-colors",
                  rateView === "fixed"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                Fixed Rates
              </button>
            </div>

            {rateView === "fixed" && (
              <div className="flex gap-2">
                {(Object.keys(fixedTermLabels) as FixedTerm[]).map((term) => (
                  <button
                    key={term}
                    onClick={() => setFixedTerm(term)}
                    className={cn(
                      "px-3 py-1.5 text-sm rounded-lg transition-colors",
                      fixedTerm === term
                        ? "bg-accent text-accent-foreground"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {fixedTermLabels[term]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Rate Cards */}
          <div className="space-y-4">
            {sortedRates.map((lender, index) => {
              // Use API logo or fall back to config lookup
              const logoUrl = lender.logo || getLenderLogo(lender.lender);
              return (
              <div
                key={lender.lender}
                className={cn(
                  "relative bg-background rounded-2xl border border-border p-6 transition-shadow hover:shadow-md",
                  index === 0 && "ring-2 ring-accent"
                )}
              >
                {/* Lowest Badge - positioned in corner */}
                {index === 0 && (
                  <span className="absolute -top-2 -left-2 text-xs bg-accent text-accent-foreground px-2 py-1 rounded-full font-medium shadow-sm">
                    Lowest
                  </span>
                )}

                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  {/* Lender Info */}
                  <div className="flex items-center justify-center md:w-40 shrink-0">
                    {logoUrl && !logoUrl.includes("unnamed.png") ? (
                      <img
                        src={logoUrl}
                        alt={lender.lender}
                        className="h-10 w-auto max-w-[140px] object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.nextElementSibling?.classList.remove("hidden");
                        }}
                      />
                    ) : null}
                    <span className={logoUrl && !logoUrl.includes("unnamed.png") ? "hidden" : "text-sm font-semibold text-foreground"}>{lender.lender}</span>
                  </div>

                  {/* Rate */}
                  <div className="flex-1 flex flex-wrap gap-6">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {rateView === "variable" ? "Variable Rate" : `${fixedTermLabels[fixedTerm]} Fixed`}
                      </p>
                      <p className="text-2xl font-bold text-primary">
                        {rateView === "variable"
                          ? lender.variableRate.toFixed(2)
                          : lender.fixedRates[fixedTerm].toFixed(2)}
                        %
                      </p>
                    </div>
                    {lender.comparisonRate > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Comparison Rate</p>
                        <p className="text-lg font-medium">{lender.comparisonRate.toFixed(2)}%</p>
                      </div>
                    )}
                    {lender.maxLVR > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Max LVR</p>
                        <p className="text-lg font-medium">{lender.maxLVR}%</p>
                      </div>
                    )}
                    {lender.loanType && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Type</p>
                        <p className="text-lg font-medium capitalize">
                          {lender.loanType === "both" ? "All" : lender.loanType}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  {lender.features && lender.features.length > 0 && (
                    <div className="md:w-64">
                      <div className="flex flex-wrap gap-2">
                        {lender.features.slice(0, 2).map((feature) => (
                          <span
                            key={feature}
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                          >
                            <FiCheck className="size-3 text-green-600" />
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              These are just a sample of our 70+ lender panel. We'll find the best rate for your situation.
            </p>
            <Button asChild className="btn-pill">
              <Link to="/contact">
                Get a Personalised Quote
                <FiArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* RBA History */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-muted/30 to-background">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl md:text-4xl mb-3">RBA Cash Rate History</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Track the Reserve Bank of Australia's official cash rate decisions and their impact on home loan rates.
            </p>
          </div>

          <div className="bg-background rounded-2xl p-6 md:p-8 shadow-sm border border-border/50">
            <CashRateChart data={cashRateHistory} />
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="bg-background rounded-xl p-4 border border-border/50 text-center">
              <p className="text-2xl font-bold text-primary">
                {currentCashRate.toFixed(2)}%
              </p>
              <p className="text-sm text-muted-foreground">Current Cash Rate</p>
            </div>
            <div className="bg-background rounded-xl p-4 border border-border/50 text-center">
              <p className="text-2xl font-bold text-green-600 flex items-center justify-center gap-1">
                <FiTrendingDown className="size-5" />
                {rateReduction}%
              </p>
              <p className="text-sm text-muted-foreground">Reduction Since Peak</p>
            </div>
            <div className="bg-background rounded-xl p-4 border border-border/50 text-center">
              <p className="text-2xl font-bold text-muted-foreground">{cashRateHistory.length}</p>
              <p className="text-sm text-muted-foreground">Rate Decisions Tracked</p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            The RBA has held rates steady since August 2025 after three consecutive cuts.
            Market expectations suggest potential for further easing in 2026 if inflation remains contained.
          </p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <p className="text-xs text-muted-foreground">
            <strong>Important:</strong> Interest rates displayed are for owner-occupier loans with
            principal and interest repayments. Rates are subject to change without notice and may
            vary based on loan amount, LVR, and individual circumstances. Comparison rates are based
            on a $150,000 loan over 25 years. Contact us for current rates and personalised quotes.
          </p>
        </div>
      </section>
    </>
  );
}
